import { useState, type FormEvent } from "react";
import { AlertCircle } from "lucide-react";
import { Botao } from "@/componentes/Botao";
import { Campo } from "@/componentes/Campo";
import { Picote } from "@/componentes/Picote";
import { textoDoErro } from "@/api/problema";
import { useSessao } from "@/sessao/useSessao";
import { ambienteAtual, ROTULO } from "@/ambiente";

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ E-MAIL E SENHA. SEM SEGUNDO FATOR — E ISSO É DECISÃO REGISTRADA.         │
 * │                                                                          │
 * │ O doc 06 §2.1 exigia TOTP obrigatório. O doc 06b §5 registrou as duas    │
 * │ saídas possíveis, e a change 20c executou a segunda: remover o fator,    │
 * │ com o risco aceito por escrito (emenda no doc 06 §2.1 e D-256).          │
 * │                                                                          │
 * │ O que isso quer dizer para esta tela: a senha digitada aqui é a          │
 * │ credencial COMPLETA de um painel que lê PII de sacado de todos os        │
 * │ tenants e cancela boleto. Não há segunda barreira depois dela.           │
 * │                                                                          │
 * │ O que NÃO mudou: as causas de recusa continuam respondendo               │
 * │ identicamente — e-mail inexistente, senha errada e operador desativado   │
 * │ são indistinguíveis, inclusive no tempo. Não tente ser específico aqui.  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function Entrar() {
  const { entrar } = useSessao();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const ambiente = ambienteAtual();

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setEnviando(true);

    try {
      await entrar({ email, senha });
    } catch (falha) {
      /*
       * A mensagem vem da API e é a MESMA para todas as causas de recusa. Não
       * tente ser específico aqui: distinguir "senha errada" de "operador não
       * existe" devolve ao atacante exatamente o oráculo que o passo único
       * eliminou. O `detail` que a API manda já diz o que fazer.
       */
      setErro(textoDoErro(falha));
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-breu px-4">
      {/*
        A linha digitável como motivo gráfico de fundo, em fio muito tênue
        (doc 06a §4.2). Sem ilustração e sem frase motivacional.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 flex items-center justify-center overflow-hidden"
      >
        <span className="dados whitespace-nowrap text-[6vw] leading-none text-texto opacity-[0.025]">
          75691.12345 67890.123456 78901.234567 8 98760000012345
        </span>
      </div>

      <div className="relative w-full max-w-[368px] rounded-cartao border border-fio bg-superficie p-6">
        <header className="flex items-baseline justify-between">
          <h1 className="text-titulo font-semibold text-texto">Boletos Maker</h1>
          <span className="rotulo">{ROTULO[ambiente]}</span>
        </header>

        <Picote className="my-5" />

        <form onSubmit={(evento) => void enviar(evento)} className="flex flex-col gap-4">
          <Campo
            rotulo="E-mail"
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
          />

          <Campo
            rotulo="Senha"
            type="password"
            name="senha"
            autoComplete="current-password"
            required
            value={senha}
            onChange={(evento) => setSenha(evento.target.value)}
          />

          {erro !== null && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-controle border border-falha/40 bg-falha/10 px-3 py-2 text-[12px] text-falha"
            >
              <AlertCircle aria-hidden="true" className="mt-[1px] size-3.5 shrink-0" />
              {erro}
            </p>
          )}

          {/*
            `type="submit"` porque este é o botão do formulário: sem ele, Enter
            no campo de senha não envia, e é assim que a maioria entra.
          */}
          <Botao type="submit" carregando={enviando} className="mt-1 h-9 w-full">
            Entrar
          </Botao>
        </form>
      </div>
    </main>
  );
}
